// src/common/commentModal.jsx
import React, { use, useEffect, useRef, useState } from "react";
import { RecruiterApi } from "../supports/recruiterApi";
import { generateRequestId } from "./common";
import unifiedAuthManager from "../services/unifiedAuthManager";
import { Pencil, Trash } from "lucide-react";
import { useToast } from "./toastSystem";

const CommentModal = ({
  isOpen,
  title = "",
  comment,
  setComment,
  onSave,
  onClose,
  candidateId,
  jobId,
  onUpdateCandidate
}) => {
  const modalRef = useRef(null);
  const deleteModalRef = useRef(null);
  const user = unifiedAuthManager.getUnifiedUser();
  const recruiterId = user?.id;
  
  const [existingComments, setExistingComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [localComment, setLocalComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const editTextareaRef = useRef(null);
  const toast = useToast()
  // Sync local comment with prop when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalComment(comment || "");
    }
  }, [isOpen, comment]);

useEffect(() => {
  const handleClickOutside = (e) => {
    console.log(editingCommentId);
    
    // Block if editing or delete modal open
    if (editingCommentId !== null || deleteConfirmId !== null) {
      e.stopPropagation();  // ✅ Extra protection
      return;
    }
    
    // Only close if NOT clicking modal content
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      e.stopPropagation();  // ✅ Prevent card clicks
      onClose();
    }
    console.log(deleteModalRef);
    
  };

  if (isOpen) {
    document.addEventListener("mousedown", handleClickOutside);
    document.body.style.overflow = 'hidden';
  }
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.body.style.overflow = 'unset';
  };
}, [isOpen, onClose, editingCommentId, deleteConfirmId]);



useEffect(() => {
  if (editingCommentId && editTextareaRef.current) {
    try {
      editTextareaRef.current.focus();
      // Optional: Move cursor to end of text
      const textarea = editTextareaRef.current;
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    } catch (error) {
      console.error('Error focusing edit textarea:', error);
      // Silently fail - focus errors are non-critical
    }
  }
}, [editingCommentId]);


// Handle click outside delete modal - prevent propagation to cards below
useEffect(() => {
  const handleDeleteModalClickOutside = (e) => {
    if (deleteConfirmId === null) return;
    
    // If delete modal is open and click is outside of it, close the delete modal
    if (deleteModalRef.current && !deleteModalRef.current.contains(e.target)) {
      e.stopPropagation();
      e.preventDefault();
      cancelDeleteComment();
    }
  };

  if (deleteConfirmId !== null) {
    document.addEventListener("mousedown", handleDeleteModalClickOutside, true);
  }
  
  return () => {
    document.removeEventListener("mousedown", handleDeleteModalClickOutside, true);
  };
}, [deleteConfirmId]);




  // Fetch existing comments function (extracted for reuse)
  const fetchComments = async () => {
    if (!candidateId) return;
    
    setIsLoading(true);
    setError(null);
    
    const requestBody = {
      candidate_id: candidateId,
      user_payload: {
        request_id: generateRequestId(),
        email: user?.email,
        tenant: "MPH",
        user_id: parseInt(recruiterId, 10)
      }
    };
    
    try {
      const response = await RecruiterApi.getComment(requestBody);
      console.log("Fetched comments response:", response);
      if (response && response.status_code === 201 && response.data) {
        setExistingComments(response.data.comments || []);
      } else if (response && response.success && response.data) {
        setExistingComments(response.data.comments || []);
      } else {
        setExistingComments([]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError("Failed to load comments");
        console.error("Error fetching comments:", err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch existing comments when modal opens
  useEffect(() => {
    if (isOpen && candidateId) {
      fetchComments();
    }
  }, [isOpen, candidateId, jobId, recruiterId]);

  const saveComments = async () => {
    if (!candidateId || localComment.trim() === "") return;
    
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    const requestBody = {
      candidate_id: candidateId,
      user_payload: {
        request_id: generateRequestId(),
        email: user?.email,
        tenant: "MPH",
        user_id: parseInt(recruiterId, 10)
      },
      job_id: jobId,
      comments: localComment
    };
    
    try {
      const response = await RecruiterApi.saveComment(requestBody);
      console.log("Save comment response:", response);

      if (response && response.status_code === 201) {
        // Store the comment text before clearing
        const savedComment = localComment;
        
        // Clear the local comment input immediately
        setLocalComment("");
        
        // Also clear parent comment if setComment is provided
        if (setComment && typeof setComment === 'function') {
          setComment("");
        }
        toast.success(response.message)
        
        // Refresh comments list after saving
        await fetchComments();

        if (onUpdateCandidate && typeof onUpdateCandidate === 'function') {
        onUpdateCandidate({ candidateId, updatedCount: existingComments.length + 1 });
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // DO NOT call onSave - it might close the modal
        // If you need to notify parent, uncomment below but test carefully
        // if (onSave && typeof onSave === 'function') {
        //   onSave(savedComment);
        // }
      } else {
        setError("Failed to save comment");
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // setError("Failed to save comment");
        console.error("Error saving comment:", err);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleEditComment = (commentItem) => {
    setEditingCommentId(commentItem.comment_id);
    setEditingCommentText(commentItem.comment || commentItem.text || "");
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const handleSaveEditedComment = async () => {
    if (!editingCommentId || editingCommentText.trim() === "") {
      setError("Comment cannot be empty");
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    const requestBody = {
      candidate_id: candidateId,
      user_payload: {
        request_id: generateRequestId(),
        email: user.email,
        tenant: "MPH",
        user_id: parseInt(recruiterId, 10)
      },
      comment_id: editingCommentId,
      new_comment: editingCommentText?.trim()
    };

    try {
      const response = await RecruiterApi.editComment?.(requestBody);
      
      if (response && (response.status_code === 201 || response.success)) {
        toast.success(response.message || "Comment updated successfully!")
        // setSuccessMessage("Comment updated successfully!");
        setEditingCommentId(null);
        setEditingCommentText("");
        await fetchComments();
      } else {
        toast.warning(response.message || "Failed to update comment")
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Error updating comment:", err);
        toast.warning("Failed to update comment")
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteComment = (commentId) => {
    setDeleteConfirmId(commentId);
  };

  const confirmDeleteComment = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    setError(null);
    setSuccessMessage(null);

    const requestBody = {
      user_payload: {
        request_id: generateRequestId(),
        email: user.email,
        tenant: "MPH",
        user_id: parseInt(recruiterId, 10),
      },
      comment_id: deleteConfirmId,
    };

    try {
      const response = await RecruiterApi.deleteComment?.(requestBody);

      if (response && (response.status_code === 201 || response.success)) {
        toast.success(response.message || "Comment deleted successfully!");
        setDeleteConfirmId(null);
        setExistingComments((prevComments) => {
          const updatedComments = prevComments.filter(
            (comment) => comment.comment_id !== deleteConfirmId
          );
          return updatedComments;
        });
        if (onUpdateCandidate && typeof onUpdateCandidate === "function") {
          onUpdateCandidate({
            candidateId,
            updatedCount: existingComments.length - 1,
          });
        }
      } else {
        toast.warning(response.message || "Failed to delete comment");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error deleting comment:", err);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteComment = () => {
    setDeleteConfirmId(null);
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .modal-content {
          animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .no-focus-outline:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}
      `}</style>

      <div
        onClick={(e) => {
    // ✅ CRITICAL: Stop propagation to prevent cards opening
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onClose();
  }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 anim-slideDown"
        style={{
          background: "rgba(0, 0, 0, 0.4)",
          // backdropFilter: 'blur(4px)',
          // animation: 'fadeIn 0.2s ease-out'
        }}
        role="dialog"
        aria-modal="true"
      >
        <div
          ref={modalRef}
          onClick={
            (e)=>{
               e.stopPropagation();
    e.preventDefault();
            }
          }
          className="modal-content bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative flex flex-col max-h-[90vh]"
          style={{
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-2 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Share your feedback
              </p>
            </div>
            <button
              onClick={()=>{
                onClose()
                handleCancelEdit()
              }}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-2 scrollbar-thin">
            {/* Success Message */}
            {successMessage && (
              <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-100 rounded-lg text-green-600 text-sm mb-5">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M5 8L7 10L11 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {successMessage}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm mb-5">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M8 4V8M8 11V11.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {error}
              </div>
            )}

            {/* Previous Comments */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-4">
                <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-3"></div>
                <p className="text-sm text-gray-500">
                  Checking for comments...
                </p>
              </div>
            ) : existingComments.length > 0 ? (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Previous Comments
                  </h3>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                    {existingComments.length}
                  </span>
                </div>
                <div className="space-y-4 max-h-96 overflow-y-auto scrollbar-thin pr-2">
                  {existingComments.map((commentItem, index) => (
                    <div key={index}>
                      {editingCommentId === commentItem.comment_id ? (
                        // Edit mode
                        <div className="flex gap-3 p-3 rounded-xl  border-2 border-gray-200 ">
                          <div className="flex-1">
                            {/* <p className="text-xs font-semibold text-gray-900 mb-2">Edit Comment</p> */}
                            <textarea
                            ref={editTextareaRef}
                              className="no-focus-outline text-xs w-full rounded-lg p-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none  resize-none focus-visible:outline-none focus:ring-0"
                              rows={3}
                              value={editingCommentText}
                              onChange={(e) =>
                                setEditingCommentText(e.target.value)
                              }
                              maxLength={500}
                            />
                            <div className="flex gap-2 mt-2 items-center justify-between">
                              <span className="text-xs text-gray-500">
                                {500 - editingCommentText.length} characters
                                left
                              </span>
                              <div className="flex gap-2">
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-xs px-4 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                                  disabled={isSaving}
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveEditedComment}
                                  className="text-xs px-4 py-1 rounded-full border-gray-300 border profiler-bg text-white hover:bg-gray-50  transition-colors disabled:opacity-50"
                                  disabled={
                                    isSaving || editingCommentText.trim() === ""
                                  }
                                >
                                  {isSaving ? "Saving..." : "Save"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex gap-3 p-2 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                              {getInitials(
                                commentItem.user_first_name +
                                  " " +
                                  commentItem.user_last_name
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-semibold text-gray-900">
                                {commentItem.user_first_name +
                                  " " +
                                  commentItem.user_last_name || "Unknown User"}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(
                                  commentItem.created_at || commentItem.date
                                )}
                              </span>
                              {
                                Number(user.id) === Number(commentItem?.user_id) && (
                                <div className="ml-auto flex items-center gap-2">
                                <button
                                  onClick={() => handleEditComment(commentItem)}
                                  className="p-1 text-gray-400 hover:text-gray-700 rounded transition-colors"
                                  title="Edit comment"
                                  aria-label="Edit"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteComment(
                                      commentItem?.comment_id || index
                                    )
                                  }
                                  className="p-1 text-gray-400 hover:text-gray-700 rounded transition-colors"
                                  title="Delete comment"
                                  aria-label="Delete"
                                >
                                  <Trash size={14} />
                                </button>
                              </div>
                                )
                              }
                          
                            </div>
                            <p className="text-xs text-gray-700 leading-relaxed break-words">
                              {commentItem.comment || commentItem.text}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          {/* Add New Comment - Sticky */}
          {!editingCommentId && (
            <div className="px-6 py-2 border-gray-100 bg-white">
              <div className="relative">
                <textarea
                  className="text-xs w-full border-2 border-gray-200 rounded-xl p-4 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 transition-all resize-none disabled:bg-gray-50 disabled:text-gray-500"
                  rows={6}
                  value={localComment}
                  maxLength={500}
                  onChange={(e) => setLocalComment(e.target.value)}
                  placeholder="Write your comment here..."
                  autoFocus
                  disabled={isSaving}
                />
                <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                  {500 - localComment?.length || 0} characters left
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          {!editingCommentId && (
            <div className="flex items-center justify-between px-6 pb-2 border-gray-100 rounded-b-2xl">
              <p className="text-xs text-gray-500">
                {/* Comments are visible to your team members */}
              </p>
              <div className="flex gap-3">
                <button
                  className="text-xs px-4 py-2 rounded-full border profiler-bg border-gray-300 inline-flex items-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                  onClick={saveComments}
                  type="button"
                  disabled={localComment.trim() === "" || isSaving}
                >
                  {isSaving ? (
                    <>
                      <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-40"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            cancelDeleteComment();
          }}
        >
          <div
            ref={deleteModalRef}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Comment?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this comment? 
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDeleteComment}
                className="text-xs px-4 py-2 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log("Delete button clicked");
                  confirmDeleteComment();
                }}
                className="text-xs px-4 py-2 rounded-full profiler-bg text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommentModal;